import { JsonApiRequestError } from '@farfetched/core';
import { ComboboxItem } from '@mantine/core';
import { combine, createStore, sample, Store } from 'effector';
import { debounce } from 'patronum';
import {
	createDeviceRegistryQuery,
	createGetAccountsQuery,
	createGetCashdeskAllQuery,
	createGetGamesQuery,
	DeviceRegistryFilters,
	DeviceDetailed,
} from 'shared/api';
import { createRegistryModel, RegistryCallbacks, RegistryModel, SORT_DIRECTIONS } from 'shared/lib';

type CreateDeviceRegistryModelParams = {
	$currencies: Store<string[]>;
	$accountId?: Store<number | null>;
	$cashdeskId?: Store<number | null>;
	$allAccountIds?: Store<number[]>;
} & RegistryCallbacks;

export type DeviceRegistryModel = {
	model: RegistryModel<DeviceDetailed, DeviceRegistryFilters, JsonApiRequestError>;
	$accounts: Store<ComboboxItem[]>;
	$cashdesks: Store<ComboboxItem[]>;
	$games: Store<ComboboxItem[]>;
	$currencies: Store<string[]>;
};

export const createDeviceRegistryModel = ({
	$currencies,
	$accountId,
	$cashdeskId,
	$allAccountIds,
	...params
}: CreateDeviceRegistryModelParams): DeviceRegistryModel => {
	const query = createDeviceRegistryQuery($accountId, $cashdeskId);

	const getAccountsQuery = createGetAccountsQuery();

	const getCashdeskAllQuery = createGetCashdeskAllQuery();

	const getGamesQuery = createGetGamesQuery();

	const model = createRegistryModel({
		query,
		filters: {
			name: '',
			cashdeskCode: '',
			accountIds: [],
			gameIds: [],
			cashdeskIds: [],
			currencies: [],
			isActive: [],
		},
		sortField: 'name',
		sortDirection: SORT_DIRECTIONS.ASC,
		...params,
	});

	const $accounts = getAccountsQuery.$data.map((accounts) =>
		accounts ? accounts.map((account) => ({ value: `${account.id}`, label: account.name })) : [],
	);

	const $cashdesks = getCashdeskAllQuery.$data.map((cashdesks) =>
		cashdesks
			? cashdesks.map((cashdesk) => ({ value: `${cashdesk.id}`, label: cashdesk.name }))
			: [],
	);

	const $games = getGamesQuery.$data.map((games) =>
		games ? games.map((game) => ({ value: `${game.id}`, label: game.title })) : [],
	);

	const $filterAccountIds = combine(
		model.filters.accountIds.$value,
		$allAccountIds ?? createStore<number[]>([]),
		(selected, all) => (selected.length > 0 ? selected : all),
	);

	sample({
		clock: model.gate.open,
		source: $filterAccountIds,
		filter: (ids) => ids.length > 0,
		target: getCashdeskAllQuery.start,
	});

	sample({
		clock: debounce($filterAccountIds, 300),
		source: model.gate.status,
		filter: (isOpen, ids) => isOpen && ids.length > 0,
		fn: (_, ids) => ids,
		target: getCashdeskAllQuery.start,
	});

	sample({
		clock: getCashdeskAllQuery.finished.success,
		source: { all: $cashdesks, current: model.filters.cashdeskIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashdesk) => current.includes(Number(cashdesk.value)))
				.map((cashdesk) => Number(cashdesk.value)),
		target: model.filters.cashdeskIds.setValue,
	});

	sample({
		clock: model.gate.open,
		target: [getAccountsQuery.start, getGamesQuery.start],
	});

	sample({
		clock: model.gate.close,
		target: [getAccountsQuery.reset, getCashdeskAllQuery.reset, getGamesQuery.reset],
	});

	return { model, $accounts, $cashdesks, $games, $currencies };
};
